import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '#environments';
import {
  AuditLogEntry,
  ContactDto,
  CreateNotificationPreferenceTypeRequest,
  FotDto,
  GraphUserDto,
  NotificationPreferenceTypeDto,
  ProvisionUserRequest,
  UpdateFotAssignmentRequest,
  UpdateNotificationPreferenceTypeRequest,
  UpdatePreferencesRequest,
  UserListItemDto,
  UserListParams,
  UserProfileDto,
} from '#models';
import { PagedList } from '#models';

@Injectable({
  providedIn: 'root',
})
export class UserManagementApiService {
  private _http = inject(HttpClient);
  private readonly _baseUrl = environment.apiUrl;

  getMyProfile(): Observable<UserProfileDto> {
    return this._http.get<UserProfileDto>(`${this._baseUrl}/me`);
  }

  getContacts(caseNumber?: string): Observable<ContactDto[]> {
    const params = caseNumber ? new HttpParams().set('caseNumber', caseNumber) : undefined;
    return this._http.get<ContactDto[]>(`${this._baseUrl}/users/contacts`, { params });
  }

  getContactInformation(caseId?: number): Observable<ContactDto[]> {
    const params = caseId != null ? new HttpParams().set('caseId', caseId) : undefined;
    return this._http.get<ContactDto[]>(`${this._baseUrl}/users/contactinformation`, { params });
  }

  getAuditLogByCaseId(caseId: number): Observable<AuditLogEntry[]> {
    return this._http.get<AuditLogEntry[]>(
      `${this._baseUrl}/cases/${caseId}/audit-log`);
  }

  updateMyFots(request: UpdateFotAssignmentRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/me/fots`, request);
  }

  updateMyPreferences(request: UpdatePreferencesRequest): Observable<void> {
    return this._http.put<void>(`${this._baseUrl}/me/preferences`, request);
  }

  getUsers(params: UserListParams): Observable<PagedList<UserListItemDto>> {
    let httpParams = new HttpParams()
      .set('page', params.pageNumber.toString())
      .set('pageSize', params.pageSize.toString());
    if (params.searchTerm) {
      httpParams = httpParams.set('search', params.searchTerm);
    }
    if (params.roleKeyFilter) {
      httpParams = httpParams.set('roleKey', params.roleKeyFilter);
    }
    return this._http.get<PagedList<UserListItemDto>>(
      `${this._baseUrl}/users`,
      { params: httpParams },
    );
  }

  provisionUser(request: ProvisionUserRequest): Observable<UserProfileDto> {
    return this._http.post<UserProfileDto>(`${this._baseUrl}/users`, request);
  }

  updateUserFots(
    userId: number,
    request: UpdateFotAssignmentRequest,
  ): Observable<void> {
    return this._http.put<void>(
      `${this._baseUrl}/users/${userId}/fots`,
      request,
    );
  }

  searchGraphUsers(query: string): Observable<GraphUserDto[]> {
    const params = new HttpParams().set('q', query);
    return this._http.get<GraphUserDto[]>(
      `${this._baseUrl}/graph/users/search`,
      { params },
    );
  }

  searchAppUsers(query: string): Observable<GraphUserDto[]> {
    const params = new HttpParams().set('q', query);
    return this._http.get<GraphUserDto[]>(
      `${this._baseUrl}/users/search`,
      { params },
    );
  }

  getGraphUser(entraId: string): Observable<GraphUserDto> {
    return this._http.get<GraphUserDto>(
      `${this._baseUrl}/graph/users/${entraId}`,
    );
  }

  getFots(): Observable<FotDto[]> {
    return this._http.get<FotDto[]>(`${this._baseUrl}/fots`);
  }

  getNotificationPreferences(): Observable<NotificationPreferenceTypeDto[]> {
    return this._http.get<NotificationPreferenceTypeDto[]>(
      `${this._baseUrl}/notification-preferences`,
    );
  }

  createNotificationPreference(
    request: CreateNotificationPreferenceTypeRequest,
  ): Observable<NotificationPreferenceTypeDto> {
    return this._http.post<NotificationPreferenceTypeDto>(
      `${this._baseUrl}/notification-preferences`,
      request,
    );
  }

  updateNotificationPreference(
    id: number,
    request: UpdateNotificationPreferenceTypeRequest,
  ): Observable<NotificationPreferenceTypeDto> {
    return this._http.put<NotificationPreferenceTypeDto>(
      `${this._baseUrl}/notification-preferences/${id}`,
      request,
    );
  }

  deactivateNotificationPreference(id: number): Observable<void> {
    return this._http.delete<void>(
      `${this._baseUrl}/notification-preferences/${id}`,
    );
  }

  exportUsersCsv(): Observable<Blob> {
    return this._http.get(`${this._baseUrl}/users/export`, {
      responseType: 'blob',
    });
  }

  getAppRoles(): Observable<string[]> {
    return this._http.get<string[]>(`${this._baseUrl}/roles`);
  }
}
